/**
 * API Response Adapters
 * 
 * Convert backend snake_case responses to frontend camelCase types.
 * Centralized here to keep App.tsx clean and ensure consistent transformation.
 */

import { 
  ScanResult, Policy, User, Finding, 
  TargetType, ScanPhase, GapType, AIAsset, RedTeamAttack,
  RiskDriver, UseCaseClassification,
  RiskBandValues, TargetTypeValues, ScanPhaseValues
} from '../types';

/**
 * Adapt a single scan API response to frontend ScanResult type
 */
export const adaptScanResponse = (scan: any): ScanResult => {
  // Calculate risk score - use risk_score if present, otherwise convert from compliance_score
  const riskScore = scan.risk_score !== undefined 
    ? scan.risk_score 
    : (100 - (scan.compliance_score || 0));
  
  // Determine risk band
  const riskBand = scan.risk_band || (
    riskScore <= 25 ? RiskBandValues.LOW :
    riskScore <= 50 ? RiskBandValues.MODERATE :
    riskScore <= 80 ? RiskBandValues.HIGH : RiskBandValues.CRITICAL
  );
  const riskSummary = scan.risk_summary ? {
    criticalCount: scan.risk_summary.critical_count || 0,
    highCount: scan.risk_summary.high_count || 0,
    mediumCount: scan.risk_summary.medium_count || 0,
    lowCount: scan.risk_summary.low_count || 0,
    topRiskAreas: scan.risk_summary.top_risk_areas || [],
    recommendations: scan.risk_summary.recommendations || [],
  } : undefined;

  // Adapt findings with gap analysis fields
  const findings: Finding[] = adaptFindings(scan.findings);

  // Adapt red team attacks
  const redTeamAttacks: RedTeamAttack[] = scan.red_team_attacks?.map((a: any) => ({
    id: a.id,
    attack_type: a.attack_type || a.type,
    prompt: a.prompt,
    expected_outcome: a.expected_outcome,
    success_likelihood: a.success_likelihood
  })) || [];

  // Adapt detected assets
  const detectedAssets: AIAsset[] = scan.detected_assets?.map((a: any) => ({
    id: a.id,
    name: a.name,
    type: a.type || a.asset_type,
    description: a.description,
    riskLevel: a.risk_level,
    applicationId: a.application_id,
    tenantId: a.tenant_id
  })) || [];

  // Adapt inherent risk drivers
  const inherentRiskDrivers: RiskDriver[] | undefined = scan.inherent_risk_drivers?.map((d: any) => ({
    factor: d.factor,
    points: d.points,
    description: d.description,
    category: d.category
  }));

  // Adapt use case classification
  const useCaseClassification: UseCaseClassification | undefined = scan.use_case_classification ? {
    deploymentType: scan.use_case_classification.deployment_type,
    dataSensitivity: scan.use_case_classification.data_sensitivity,
    privilegeLevel: scan.use_case_classification.privilege_level,
    operations: scan.use_case_classification.operations,
    industryContext: scan.use_case_classification.industry_context,
    modalities: scan.use_case_classification.modalities
  } : undefined;

  return {
    id: scan.id,
    timestamp: new Date(scan.created_at).getTime(),
    applicationId: scan.application_id,
    applicationName: scan.application_name,
    targetName: scan.target_name,
    
    // Target info
    targetType: (scan.target_type as TargetType) || TargetTypeValues.DESCRIPTION,
    githubUrl: scan.github_url,
    githubBranch: scan.github_branch,
    githubCommitSha: scan.github_commit_sha,
    
    // Phase tracking
    phase: (scan.phase as ScanPhase) || ScanPhaseValues.COMPLETED,
    phaseMessage: scan.phase_message,
    
    // Risk scoring
    riskScore,
    riskBand,
    riskSummary,
    
    // Legacy field for backwards compatibility
    complianceScore: scan.compliance_score || (100 - riskScore),
    
    // Counts
    assetsDiscoveredCount: scan.assets_discovered_count || 0,
    findingsCount: scan.findings_count || findings.length,
    policiesAnalyzed: scan.policies_analyzed,
    
    // Application summary (from AI analysis)
    deploymentType: scan.deployment_type,
    industrySector: scan.industry_sector,
    modalities: scan.modalities,
    useCaseSummary: scan.use_case_summary,
    
    // Phase 1B: Risk quantification fields
    inherentRiskScore: scan.inherent_risk_score,
    residualRiskScore: scan.residual_risk_score,
    inherentRiskDrivers,
    useCaseClassification,
    dataSensitivity: scan.data_sensitivity,
    privilegeLevel: scan.privilege_level,
    operations: scan.operations,
    
    // Analysis results
    findings,
    redTeamAttacks,
    detectedAssets,
    
    status: scan.status,
    tenantId: scan.tenant_id
  };
};

/**
 * Adapt scan list response (lighter version for list views)
 */
export const adaptScanListItem = (scan: any): ScanResult => {
  const riskScore = scan.risk_score !== undefined ? scan.risk_score : (100 - (scan.compliance_score || 0));
  const riskBand = scan.risk_band || (
    riskScore <= 25 ? RiskBandValues.LOW :
    riskScore <= 50 ? RiskBandValues.MODERATE :
    riskScore <= 80 ? RiskBandValues.HIGH : RiskBandValues.CRITICAL
  );
  
  // Adapt risk_summary from snake_case to camelCase
  const riskSummary = scan.risk_summary ? {
    criticalCount: scan.risk_summary.critical_count || 0,
    highCount: scan.risk_summary.high_count || 0,
    mediumCount: scan.risk_summary.medium_count || 0,
    lowCount: scan.risk_summary.low_count || 0,
    topRiskAreas: scan.risk_summary.top_risk_areas || [],
    recommendations: scan.risk_summary.recommendations || []
  } : undefined;

  // Adapt inherent risk drivers
  const inherentRiskDrivers: RiskDriver[] | undefined = scan.inherent_risk_drivers?.map((d: any) => ({
    factor: d.factor,
    points: d.points,
    description: d.description,
    category: d.category
  }));

  // Adapt use case classification
  const useCaseClassification: UseCaseClassification | undefined = scan.use_case_classification ? {
    deploymentType: scan.use_case_classification.deployment_type,
    dataSensitivity: scan.use_case_classification.data_sensitivity,
    privilegeLevel: scan.use_case_classification.privilege_level,
    operations: scan.use_case_classification.operations,
    industryContext: scan.use_case_classification.industry_context,
    modalities: scan.use_case_classification.modalities
  } : undefined;
  
  return {
    id: scan.id,
    timestamp: new Date(scan.created_at).getTime(),
    applicationId: '',
    applicationName: scan.application_name,
    targetName: scan.target_name,
    targetType: (scan.target_type as TargetType) || TargetTypeValues.DESCRIPTION,
    phase: (scan.phase as ScanPhase) || ScanPhaseValues.COMPLETED,
    riskScore,
    riskBand,
    riskSummary,
    complianceScore: scan.compliance_score || (100 - riskScore),
    assetsDiscoveredCount: scan.assets_discovered_count || 0,
    findingsCount: scan.findings_count || 0,
    // Application summary
    deploymentType: scan.deployment_type,
    industrySector: scan.industry_sector,
    modalities: scan.modalities,
    useCaseSummary: scan.use_case_summary,
    // Phase 1B fields
    inherentRiskScore: scan.inherent_risk_score,
    residualRiskScore: scan.residual_risk_score,
    inherentRiskDrivers,
    useCaseClassification,
    dataSensitivity: scan.data_sensitivity,
    privilegeLevel: scan.privilege_level,
    operations: scan.operations,
    // Empty arrays for list view
    findings: [],
    redTeamAttacks: [],
    detectedAssets: [],
    status: scan.status,
    tenantId: ''
  };
};

/**
 * Adapt findings array
 */
export const adaptFindings = (findings: any[] | undefined): Finding[] => {
  if (!findings) return [];
  
  return findings.map((f: any) => ({
    id: f.id,
    title: f.title,
    description: f.description,
    severity: f.severity,
    // Gap analysis fields
    gapType: f.gap_type as GapType | undefined,
    controlId: f.control_id,
    controlName: f.control_name,
    // Location
    affectedFile: f.affected_file,
    lineNumber: f.line_number,
    // Remediation and evidence
    remediation: f.remediation,
    evidence: f.evidence,
    evidences: f.evidences?.map((e: any) => ({
      filePath: e.file_path,
      lineNumber: e.line_number,
      codeSnippet: e.code_snippet,
      description: e.description,
    })),
    evidenceCount: f.evidence_count,
    confidenceScore: f.confidence_score,
  }));
};

/**
 * Adapt policy API response to frontend type
 */
export const adaptPolicyResponse = (policy: any): Policy => {
  const controls = policy.controls?.map((c: any) => ({
    id: c.id,
    control_id: c.control_id,
    name: c.title || c.name,  // ControlLibrary uses "title"
    title: c.title || c.name,
    description: c.description,
    category: c.category,
    ai_applicable: c.ai_applicable ?? c.enabled ?? true,
    enabled: c.enabled,
    policy_id: c.policy_id,
    created_at: c.created_at || new Date().toISOString()
  })) || [];
  
  return {
    id: policy.id,
    name: policy.name,
    description: policy.description || '',
    content: policy.content,
    enabled: policy.enabled,
    category: policy.category,
    is_custom: policy.is_custom,
    tenant_id: policy.tenant_id,
    created_at: policy.created_at || new Date().toISOString(),
    controls
  };
};

/**
 * Adapt user API response to frontend type
 */
export const adaptUserResponse = (user: any): User => ({
  id: user.id,
  email: user.email,
  name: user.name,
  role: user.role,
  scope: user.scope || 'tenant',
  avatar: user.avatar || undefined,
  tenant_name: user.tenant_name || null,
  tenant_id: user.tenant_id,
  is_active: user.is_active ?? true,
  created_at: user.created_at || new Date().toISOString(),
  subscription_status: user.subscription_status ?? null,
  billing_period_end: user.billing_period_end ?? null,
  credits_balance: user.credits_balance ?? null,
  credits_included: user.credits_included ?? null
});

